import { createSlice, createSelector } from "@reduxjs/toolkit";
import { usersApiSlice } from "./usersApiSlice";
import config from "../../config.json";

const slice = createSlice({
    name: "users",
    initialState: {
        list: [],
        loading: false,
        lastFetch: null,
        addedMessage: false, 
        emailConfig: null,
        emailConfigUpdateStatus: false,
        activatedDeactivatedStatus: false,
        accessLogHistory: [],
        mailHistory: [],
        mailSendStatus: false,
        mailDeleteStatus: false,
        mailAddStatus: false,
        mailUpdateStatus: false,
        secQuestions: [],    
        secQuestionUpdatedStatus: null
    },
    reducers: {
        usersReceived: (users, action) => {
            users.list = action.payload;
            users.addedMessage = false;
        },
        userAdded: (users, action) => {
            users.addedMessage = true;
        },
        filterUsersReceived: (users, action) => {
            users.filterList = action.payload;
        },
        emailConfigReceived: (users, action) => {
            users.emailConfig = action.payload;
            users.emailConfigUpdateStatus = false;
        },
        emailConfigUpdated: (users, action) => {
            users.emailConfigUpdateStatus = true;
        },
        userActiveDeactiveUpdated: (users, action) => {
            users.activatedDeactivatedStatus = true;
        },
        userActiveDeactiveReset: (users, action) => {
            users.activatedDeactivatedStatus = false;
        },
        userAccessLogHistoryReceived: (users, action) => {
            users.accessLogHistory = action.payload;
            users.activatedDeactivatedStatus = false;
        },
        mailHistoryReceived: (users, action) => {
            users.mailHistory = action.payload;
            users.mailSendStatus = false;
            users.mailDeleteStatus = false;
            users.mailUpdateStatus = false;
        },
        emailMessageSent: (users, action) => {
            users.mailSendStatus = action.payload ?? true;
        },
        emailMessageDeleted: (users, action) => {
            users.mailDeleteStatus = true;
        },
        emailMessageAdded: (users, action) => {
            users.mailAddStatus = action.payload;
        },
        emailMessageUdated: (users, action) => {
            users.mailUpdateStatus = action.payload;
        },
        secQuestionsReceived: (users, action) => {
            users.secQuestions = action.payload;
        },
        secQuestionsUpdated: (users, action) => {
            users.secQuestionUpdatedStatus = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(
                usersApiSlice.endpoints.getRegisteredUsers.matchFulfilled,
                (users, action) => {
                    users.list = action.payload;
                    users.addedMessage = false;
                }
            )
            .addMatcher(
                usersApiSlice.endpoints.getEmailConfig.matchFulfilled,
                (users, action) => {
                    users.emailConfig = action.payload;
                    users.emailConfigUpdateStatus = false;
                }
            )
            .addMatcher(
                usersApiSlice.endpoints.getSecQuestions.matchFulfilled,
                (users, action) => {
                    users.secQuestions = action.payload;
                }
            )
            .addMatcher(
                usersApiSlice.endpoints.getAllEmailHistory.matchFulfilled,
                (users, action) => {
                    users.mailHistory = action.payload;
                    users.mailSendStatus = false;
                    users.mailDeleteStatus = false;
                    users.mailUpdateStatus = false;
                }
            )
            .addMatcher(
                usersApiSlice.endpoints.sendEmailMessage.matchFulfilled,
                (users, action) => {
                    users.mailSendStatus = action.payload ?? true;
                }
            )
            .addMatcher(
                usersApiSlice.endpoints.deleteEmailMessage.matchFulfilled,
                (users, action) => {
                    users.mailDeleteStatus = true;
                }
            )
            .addMatcher(
                usersApiSlice.endpoints.updateEmailConfig.matchFulfilled,
                (users, action) => {
                    users.emailConfigUpdateStatus = true;
                }
            )
            .addMatcher(
                usersApiSlice.endpoints.addNewEmailMessage.matchFulfilled,
                (users, action) => {
                    users.mailAddStatus = action.payload;
                }
            )
            .addMatcher(
                usersApiSlice.endpoints.updateEmailMessage.matchFulfilled,
                (users, action) => {
                    users.mailUpdateStatus = action.payload;
                }
            )
            .addMatcher(
                usersApiSlice.endpoints.getRegisteredUsersByFilter.matchFulfilled,
                (users, action) => {
                    users.filterList = action.payload;
                }
            )
            .addMatcher(
                usersApiSlice.endpoints.addUserSecurityQuestion.matchFulfilled,
                (users, action) => {
                    users.secQuestionUpdatedStatus = action.payload;
                }
            )
            .addMatcher(
                usersApiSlice.endpoints.getUserAccessLogHistory.matchFulfilled,
                (users, action) => {
                    users.accessLogHistory = action.payload;
                    users.activatedDeactivatedStatus = false;
                }
            )
            .addMatcher(
                usersApiSlice.endpoints.updateUserActiveDeactive.matchFulfilled,
                (users, action) => {
                    users.activatedDeactivatedStatus = true;
                }
            );
    }
});

export const { usersReceived, userAdded,
    filterUsersReceived, emailConfigReceived, emailConfigUpdated,
    userActiveDeactiveUpdated, userActiveDeactiveReset, userAccessLogHistoryReceived,
    mailHistoryReceived, emailMessageSent, emailMessageDeleted,
    emailMessageAdded, emailMessageUdated, secQuestionsReceived, secQuestionsUpdated } = slice.actions;
export default slice.reducer;

export const loadRegisteredUsers = (pageSize, pageIndex, type) =>
    usersApiSlice.endpoints.getRegisteredUsers.initiate({ pageSize, pageIndex, type });

export const loadEmailConfig = () =>
    usersApiSlice.endpoints.getEmailConfig.initiate();

export const loadSecQuestions = (type) =>
    usersApiSlice.endpoints.getSecQuestions.initiate(type);

export const loadAllEmailHistory = (id) =>
    usersApiSlice.endpoints.getAllEmailHistory.initiate(id);

export const SendEmailMessage = (id) =>
    usersApiSlice.endpoints.sendEmailMessage.initiate(id);

export const DeleteEmailMessage = (id) =>
    usersApiSlice.endpoints.deleteEmailMessage.initiate(id);

export const updateEmailConfig = (data) =>
    usersApiSlice.endpoints.updateEmailConfig.initiate(data);

export const addNewEmailMessage = (data) =>
    usersApiSlice.endpoints.addNewEmailMessage.initiate(data);

export const updateEmailMessage = (data) =>
    usersApiSlice.endpoints.updateEmailMessage.initiate(data);

export const loadRegisteredUsersByFilter = (data) =>
    usersApiSlice.endpoints.getRegisteredUsersByFilter.initiate(data);

export const addUserSecurityQuestion = (data) =>
    usersApiSlice.endpoints.addUserSecurityQuestion.initiate(data);

export const loadUserAccessLogHistory = (id, type) =>
    usersApiSlice.endpoints.getUserAccessLogHistory.initiate({ id, type });

export const updateUserActiveDeactive = (data) =>
    usersApiSlice.endpoints.updateUserActiveDeactive.initiate(data);

export const getRegisteredUsers = createSelector(
    state => state.entities.users,
    users => users.list
);

export const getFilterAllUsers = createSelector(
    state => state.entities.users,
    users => users.filterList
);

export const getUserAccessHistory = createSelector(
    state => state.entities.users,
    users => users.accessLogHistory
);

export const getActivateDeactivateUpdateStatus = createSelector(
    state => state.entities.users,
    users => users.activatedDeactivatedStatus
);

export const getEmailConfig = createSelector(
    state => state.entities.users,
    users => users.emailConfig
);

export const getEmailConfigUpdateStatus = createSelector(
    state => state.entities.users,
    users => users.emailConfigUpdateStatus
);

export const getAllEmailHistory = createSelector(
    state => state.entities.users,
    users => users.mailHistory
);

export const getSentEmailStatus = createSelector(
    state => state.entities.users,
    users => users.mailSendStatus
);

export const getDeleteEmailStatus = createSelector(
    state => state.entities.users,
    users => users.mailDeleteStatus
);

export const getEmailAddedStatus = createSelector(
    state => state.entities.users,
    users => users.mailAddStatus
);

export const getEmailUpdatedStatus = createSelector(
    state => state.entities.users,
    users => users.mailUpdateStatus
);

export const getSecQuestions = createSelector(
    state => state.entities.users,
    users => users.secQuestions
);
export const getSecQuestionUpdatedStatus = createSelector(
    state => state.entities.users,
    users => users.secQuestionUpdatedStatus
);

